import {Show} from 'platform/foundation';

import type {CategoryItem} from '../../../../../../hooks/useFilterCategorySelection';
import type {EagColumnOrGroup} from '../../../../../../types/EagColumnGroup';
import type {Filters} from '../../../../../../types/Filters';
import {FilterContentArea} from '../../../FilterContentArea/FilterContentArea';

type FilterAccordionSectionProps = {
  category: CategoryItem;
  isExpanded: boolean;
  onToggle: (id: string) => void;
  columnConfig: EagColumnOrGroup[];
  filterState: Filters;
  onFilterChange: (key: string, value: unknown) => void;
  searchTerm: string;
  showActiveOnly: boolean;
};

/** Right-pointing chevron that rotates 90° when its section is expanded. */
function ChevronIcon({isExpanded}: {isExpanded: boolean}) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      className={`flex-shrink-0 text-[#7A869A] transition-transform duration-150 ${isExpanded ? 'rotate-90' : ''}`}
      aria-hidden="true"
    >
      <path
        d="M6 4l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * A single collapsible accordion card in the desktop FilterPanel: a header button
 * (chevron + category label + active-count badge + total-count) and, when expanded,
 * the category's filter widgets. Purely presentational — all state lives in FilterPanel.
 */
export function FilterAccordionSection(props: FilterAccordionSectionProps) {
  const {category, isExpanded} = props;

  return (
    <div
      className="overflow-hidden"
      style={{borderBottom: '1px solid #EBECF0'}}
      data-testid={`accordion-section-${category.id}`}
    >
      {/* Section header */}
      <button
        type="button"
        onClick={() => props.onToggle(category.id)}
        data-testid={`accordion-header-${category.id}`}
        className="w-full flex items-center gap-2 text-left hover:bg-gray-50 focus:outline-none"
        style={{padding: '8px 0'}}
      >
        <ChevronIcon isExpanded={isExpanded} />

        {/* Category name */}
        <span
          className="flex-1 text-[#091E42] truncate"
          style={{fontSize: '13px', fontWeight: 600}}
        >
          {category.label}
        </span>

        {/* Right-aligned meta cluster: active badge + total count */}
        <span className="flex-shrink-0 flex items-center gap-2">
          {/* Active count badge */}
          <Show when={category.activeCount > 0}>
            <span
              className="inline-flex items-center justify-center rounded-full bg-[#DCEFFF] text-[#0747A6]"
              style={{
                fontSize: '10px',
                fontWeight: 700,
                minWidth: '16px',
                height: '16px',
                padding: '0 5px',
                fontVariantNumeric: 'tabular-nums',
              }}
              data-testid={`active-count-badge-${category.id}`}
            >
              {category.activeCount}
            </span>
          </Show>

          {/* Total filter count */}
          <span
            className="text-right text-[11px] text-[#7A869A]"
            style={{minWidth: '16px', fontVariantNumeric: 'tabular-nums'}}
          >
            {category.childLabels.length}
          </span>
        </span>
      </button>

      {/* Section body */}
      <Show when={isExpanded}>
        <div style={{paddingBottom: '8px'}} data-testid={`accordion-body-${category.id}`}>
          <FilterContentArea
            columnConfig={props.columnConfig}
            selectedCategoryId={category.id}
            filterState={props.filterState}
            onFilterChange={props.onFilterChange}
            searchTerm={props.searchTerm}
            showActiveOnly={props.showActiveOnly}
            singleColumn
            noScroll
          />
        </div>
      </Show>
    </div>
  );
}
